<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("journal_voucher") }}</h4>
        </div>
      </div>

      <div class="page-btn">
        <router-link :to="{ name: 'JournalVoucherCreate' }" class="btn btn-added">
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ $t("add_journal_voucher") }}
        </router-link>
      </div>
    </div>

    <ThemeDataTable
      ref="journalTable"
      table-id="JournalVoucherTable"
      :fetch-data="fetchVouchersPage"
      :filters="tableFilters"
      :default-order-column="1"
      default-order-dir="desc"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("voucher_no") }}</th>
            <th>{{ $t("voucher_date") }}</th>
            <th>{{ $t("head") }}</th>
            <th>{{ $t("party") }}</th>
            <th>{{ $t("narration") }}</th>
            <th>{{ $t("debit") }}</th>
            <th>{{ $t("credit") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>{{ row.voucher_id }}</td>
            <td>{{ row.voucher_date }}</td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <span
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-book"></i>
                  </span>
                </span>
                <div>
                  <div class="text-uppercase">{{ row.head_name || '—' }}</div>
                  <small class="text-muted">{{ row.account_id }}</small>
                </div>
              </div>
            </td>
            <td>
              <span v-if="row.party_name">
                <i class="ti me-1" :class="row.party_kind === 'supplier' ? 'ti-truck' : 'ti-user'"></i>{{ row.party_name }}
              </span>
              <span v-else>—</span>
            </td>
            <td>{{ row.narration || '—' }}</td>
            <td>{{ formatMoney(row.debit) }}</td>
            <td>{{ formatMoney(row.credit) }}</td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';
import { truncateMoney2 } from '@/utils';

// Vouchers entered on the voucher pages are numbered 15YYYYMMDDNNN; POS due entries share the JR flag.
const VOUCHER_PREFIX = '15';

// thead index -> how that column sorts
const SORT_KEYS = {
  0: (row) => row.voucher_id || '',
  1: (row) => row.voucher_date || '',
  2: (row) => row.head_name || '',
  3: (row) => row.party_name || '',
  4: (row) => row.narration || '',
  5: (row) => Number(row.debit) || 0,
  6: (row) => Number(row.credit) || 0,
};

export default {
  components: {
    ThemeDataTable,
  },
  data() {
    return {
      // The endpoint returns every journal row at once, so paging, search and sort happen here.
      ledgerRows: null,
      loadingRows: null,
    };
  },
  computed: {
    headOptions() {
      return [...new Set((this.ledgerRows || []).map((row) => row.head_name).filter(Boolean))];
    },

    partyOptions() {
      return [...new Set((this.ledgerRows || []).map((row) => row.party_name).filter(Boolean))];
    },

    tableFilters() {
      return [
        {
          key: 'head',
          label: this.$t('head'),
          type: 'select2',
          options: this.headOptions,
        },
        {
          key: 'party',
          label: this.$t('party'),
          type: 'select2',
          options: this.partyOptions,
        },
      ];
    },
  },
  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    loadRows() {
      if (!this.loadingRows) {
        this.loadingRows = Promise.all([
          this.$axios.get(apiUrls.journal_voucher.get, {
            params: { flag: 'JR', status: 'Child', transaction_status: 'AV' },
          }),
          this.$axios.get(apiUrls.chart_of_accounts.get, { params: { status: 'Child' } }),
          this.$axios.get(apiUrls.customers.get),
          this.$axios.get(apiUrls.inventory_supplier.get),
        ])
          .then(([ledgerRes, headsRes, customersRes, suppliersRes]) => {
            const headNames = new Map(
              (headsRes.data || []).map((head) => [String(head.account_id), (head.node || '').trim()])
            );
            const customerNames = new Map((customersRes.data || []).map((c) => [c.id, c.customer_name]));
            const supplierNames = new Map((suppliersRes.data || []).map((s) => [s.id, s.name]));
            this.ledgerRows = (ledgerRes.data || [])
              .filter((row) => String(row.voucher_id || '').startsWith(VOUCHER_PREFIX))
              .map((row) => {
                const party = row.pos_customer_id
                  ? { party_kind: 'customer', party_name: customerNames.get(row.pos_customer_id) || '' }
                  : row.supplier_id
                    ? { party_kind: 'supplier', party_name: supplierNames.get(row.supplier_id) || '' }
                    : { party_kind: '', party_name: '' };
                return {
                  ...row,
                  ...party,
                  head_name: headNames.get(String(row.account_id)) || '',
                };
              });
          })
          .catch((error) => {
            this.loadingRows = null;
            throw error;
          });
      }
      return this.loadingRows;
    },

    async fetchVouchersPage({ start, length, search, orderColumn, orderDir, filters }) {
      await this.loadRows();
      const all = this.ledgerRows;
      let rows = all;

      if (filters.head) {
        rows = rows.filter((row) => row.head_name === filters.head);
      }
      if (filters.party) {
        rows = rows.filter((row) => row.party_name === filters.party);
      }

      const term = (search || '').toLowerCase();
      if (term) {
        rows = rows.filter((row) =>
          [row.voucher_id, row.voucher_date, row.head_name, row.account_id, row.party_name, row.narration]
            .some((value) => String(value ?? '').toLowerCase().includes(term))
        );
      }

      const key = SORT_KEYS[orderColumn] || SORT_KEYS[1];
      const dir = orderDir === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        const x = key(a);
        const y = key(b);
        const cmp = typeof x === 'number' ? x - y : String(x).localeCompare(String(y));
        return cmp * dir || (b.id - a.id);
      });

      return {
        data: rows.slice(start, start + length),
        recordsTotal: all.length,
        recordsFiltered: rows.length,
      };
    },
  },
};
</script>
